<template>
  <div class="form-container">
    <div v-if="errorText" class="errors-container">
      <p>{{ errorText }}</p>
    </div>

    <div class="section-container">
      <substrate-mount>
        <div class="option-container">
          <option-panel
            :option="formattedCountry"
            noactive
            @click="onShownCountriesModal"
          >
            <cs-button type="secondary">
              {{ $t('common.change') }}
            </cs-button>
          </option-panel>
        </div>

        <div v-if="isCountryUzbekistan" class="identification-myid">
          <p
            v-m-b="20"
            v-text="$t('indentification.form.uzbekistan.title')"
          ></p>
          <base-button
            v-m-b="20"
            @click="handleRedirectToMyId"
          >
            <img src="~images/logos/logo-myid.svg" alt="">
          </base-button>
          <p
            v-text="$t('indentification.form.uzbekistan.description')"
          ></p>
        </div>

        <a
          href="https://help.capusta.space/identification"
          target="_blank"
          rel="noopener noreferrer"
          class="information-link-container"
        >
          <p>{{ $t('how.fill.form.correctly') }}</p>
          <i></i>
        </a>

        <div class="input-container">
          <input-text
            ref="secondName"
            v-model="form.secondName"
            input-id="input-secondname"
            :input-name="$t('second.name')"
            :input-placeholder="$t('for.example.second.name')"
            :min-length="2"
            :max-length="255"
            required
          />
        </div>
        <div class="input-container">
          <input-text
            ref="firstName"
            v-model="form.firstName"
            input-id="input-firstname"
            :input-name="$t('first.name')"
            :input-placeholder="$t('for.example.first.name')"
            :min-length="2"
            :max-length="255"
            required
          />
        </div>
        <div class="input-container">
          <input-text
            v-model="form.patronymic"
            input-id="input-patronymic"
            :input-name="$t('third.name')"
            :input-placeholder="$t('for.example.third.name')"
            :required="false"
          />
        </div>
        <div class="input-container">
          <cs-input-validation
            #default="{ error }"
            key="input-birth-date"
            :value="form.birthDate"
            :validation-rules="{ date: { min: 14, max: 100 }, required: true }"
          >
            <input-text
              ref="birthDate"
              key="input-birth-date"
              v-model="form.birthDate"
              v-mask="'##.##.####'"
              :error="error"
              input-id="input-birth-date"
              :input-name="$t('birth.date')"
              :input-placeholder="$t('date.abbreviated')"
              required
            />
          </cs-input-validation>
        </div>
        <div class="input-container">
          <input-text
            v-model="form.placeOfBirth"
            input-id="input-place-of-birth"
            :input-name="$t('birth.place')"
            :input-placeholder="$t('for.example.birth.place')"
            :min-length="2"
            :max-length="255"
            required
          />
        </div>
        <div v-if="passport" class="input-container">
          <cs-input-validation
            #default="{ error }"
            key="input-document-id-validation"
            :value="form.documentId"
            :validation-rules="{ passport: passport, required: true }"
          >
            <input-text
              ref="documentId"
              key="input-document-id"
              v-model="form.documentId"
              :error="error"
              input-id="input-document-id"
              :input-name="$t(passport.label)"
              :input-placeholder="passport.placeholder"
              :min-length="2"
              :max-length="255"
              data-private
              required
            />
          </cs-input-validation>
        </div>
        <div class="input-container">
          <cs-input-validation
            #default="{ error }"
            key="input-document-issue-date-validation"
            :value="form.documentIssueDate"
            :validation-rules="{ date: { min: -1, max: 100 }, required: true }"
          >
            <input-text
              ref="documentIssueDate"
              key="input-document-issue-date"
              v-model="form.documentIssueDate"
              v-mask="'##.##.####'"
              :error="error"
              input-id="input-document-issue-date"
              :input-name="$t('passport.issue.date')"
              :input-placeholder="$t('date.abbreviated')"
              required
            />
          </cs-input-validation>
        </div>

        <div v-if="isShownInsuranceNumber" class="input-container">
          <input-text
            v-model="form.insuranceNumber"
            v-mask="'###-###-### ##'"
            input-id="input-insurance-number"
            :input-name="$t('common.snils')"
            input-placeholder="128-501-988 88"
            :min-length="14"
            :max-length="14"
            data-private
            required
          />
        </div>
      </substrate-mount>
    </div>

    <div class="button-primary-container">
      <cs-button :disabled="!isValidForm" @click="onSubmit">
        {{ $t('provide.information') }}
      </cs-button>
    </div>

    <cs-dialog v-model="isShownCountries">
      <dialog-list-layout>
        <option-panel
          v-for="country in formattedCountries"
          :key="country.id"
          :option="country"
          @click="onSelectCountry"
        />
      </dialog-list-layout>
    </cs-dialog>
  </div>
</template>

<script>
import { mask } from 'vue-the-mask';
import { isMobile } from 'mobile-device-detect';

import dateValidation from 'lib/identity/validation/date';

import OptionPanel from '@/components/capusta/OptionPanel';
import SubstrateMount from '@/components/capusta/SubstrateMount';
import InputText from '@/components/inputElements/InputText';

import BaseButton from 'atoms/base/BaseButton';

import DialogListLayout from 'modules/Dialogs/ListLayout';

import { DEFAULT_COUNTRY, PASSPORTS } from './const';

import { MYID_CLIENT_ID, MYID_REDIRECT_URI, MYID_AUTH_URI } from 'root/.env';

export default {
  name: 'IdentityForm',
  components: {
    BaseButton,
    OptionPanel,
    SubstrateMount,
    InputText,
    DialogListLayout,
  },
  directives: {
    mask,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      form: null,
      isShownCountries: false,
      isValidForm: false,
    };
  },
  computed: {
    formattedCountry() {
      const storeCountry = this.countries.find(country => this.form.country.id === country.id) || this.form.country;

      return {
        id: storeCountry.id,
        description: this.$t(storeCountry.name),
        label: this.$t('common.citizenship'),
      };
    },
    formattedCountries() {
      const activeCountryId = this.form.country.id;

      return this.countries.map(country => {
        return {
          id: country.id,
          description: this.$t(country.name),
          active: country.id === activeCountryId,
        };
      });
    },
    passport() {
      if (this.$validate.isNull(this.form)) {
        return PASSPORTS.find(item => item.alpha_2 === DEFAULT_COUNTRY.alpha_2);
      }

      return PASSPORTS.find(item => item.alpha_2 === this.form.country.alpha_2);
    },
    isShownInsuranceNumber() {
      return this.form.country.alpha_2 === 'RU';
    },
    errorText() {
      const { rejectionReason } = this.form;

      if (this.$validate.isNull(rejectionReason)) {
        return null;
      }

      const errorFields = rejectionReason.errors.map(error => `"${this.$t(error.label)}"`);
      const diclensionWordField = errorFields.length > 1 ? this.$t('common.fields') : this.$t('common.field');
      const joinedErrors = errorFields.join(', ');

      return this.$t('check.data.correctness.and.send.form.again', {
        diclensionWordField,
        joinedErrors,
      });
    },
    isCountryUzbekistan() {
      return this.form?.country.alpha_2 === 'UZ';
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.form = val;
      },
    },
    form: {
      deep: true,
      immediate: true,
      handler(val) {
        this.validationForm(val);
      },
    },
  },
  methods: {
    // eslint-disable-next-line complexity
    validationForm(data) {
      let isCheck = true;

      if (this.$validate.isNull(data.secondName) || this.$validate.isEmptyString(data.secondName) || data.secondName.length < 2 || data.secondName.length > 255) {
        isCheck = false;
      }

      if (this.$validate.isNull(data.firstName) || this.$validate.isEmptyString(data.firstName) || data.firstName.length < 2 || data.firstName.length > 255) {
        isCheck = false;
      }

      if (this.$validate.isNull(data.birthDate) || this.$validate.isEmptyString(data.birthDate) || !dateValidation(data.birthDate, { min: 14, max: 100 })) {
        isCheck = false;
      }

      if (this.$validate.isNull(data.placeOfBirth) || this.$validate.isEmptyString(data.placeOfBirth) || data.placeOfBirth.length < 2 || data.placeOfBirth.length > 255) {
        isCheck = false;
      }

      if (this.$validate.isNull(data.documentId) || this.$validate.isEmptyString(data.documentId) || !this.passport.validation.test(data.documentId)) {
        isCheck = false;
      }

      if (this.$validate.isNull(data.documentIssueDate) || this.$validate.isEmptyString(data.documentIssueDate) || !dateValidation(data.documentIssueDate, { min: -1, max: 100 })) {
        isCheck = false;
      }

      if (this.isShownInsuranceNumber && (this.$validate.isNull(data.insuranceNumber) || data.insuranceNumber.length !== 14)) {
        isCheck = false;
      }

      this.isValidForm = isCheck;
    },
    onShownCountriesModal() {
      this.isShownCountries = true;
    },
    async onSelectCountry(data) {
      this.validationForm(this.form);

      const selectedCountry = this.countries
        .find(country => country.id === data.id);

      this.isShownCountries = false;
      this.$emit('change-country', selectedCountry);
    },
    onSubmit() {
      if (!this.isValidForm) {
        this.$alert({
          type: 'warning',
          text: 'mistakes.in.form',
        });
        return;
      }

      this.$emit('submit', {
        country: this.form.country,
        firstName: this.form.firstName,
        secondName: this.form.secondName,
        patronymic: this.form.patronymic || null,
        birthDate: this.getFormattedDate(this.form.birthDate),
        placeOfBirth: this.form.placeOfBirth,
        documentId: this.form.documentId,
        documentIssueDate: this.getFormattedDate(this.form.documentIssueDate),
        insuranceNumber: this.getFormattedInsuranceNumber(this.form.insuranceNumber),
        type: this.form.type,
      });
    },
    getFormattedDate(date) {
      if (this.$validate.isEmptyString(date)) {
        return null;
      }

      return date.split('.').reverse().join('-');
    },
    getFormattedInsuranceNumber(value) {
      if (this.$validate.isNotString(value)) {
        return null;
      }

      return this.isShownInsuranceNumber ? value.replaceAll('-', '').replace(' ', '') : null;
    },
    handleRedirectToMyId() {
      const AUTHORIZATION_URL = isMobile
        ? 'uz.uzinfocom.myid://authorization'
        : MYID_AUTH_URI;

      window.location.assign(`${AUTHORIZATION_URL}?client_id=${MYID_CLIENT_ID}&response_type=code&redirect_uri=${MYID_REDIRECT_URI}&scope=address,contacts,doc_data,common_data&method=strong&state=xyzABC123`);
    },
  },
};
</script>

<style lang="sass" scoped>
  .form-container
    width: 100%
    max-width: 500px
    position: relative
    margin: 0 auto

  .errors-container
    position: relative
    padding: 24px 20px
    font-weight: 500
    font-size: 16px
    line-height: 20px
    color: #fff
    background-color: #FF5172

    +respond-to('small')
      bottom: -10px
      padding-bottom: 30px
      border-radius: 10px 10px 0 0

  .section-container
    margin-bottom: 20px

  .input-container
    .input,
    .textarea
      margin: 0

  .information-link-container
    display: flex
    align-items: stretch
    justify-content: space-between
    padding: 0 18px
    font-weight: 500
    font-size: 16px
    line-height: 20px
    color: #7CB342

    i
      width: 22px
      height: 22px
      flex: none
      display: block
      background-image: url('~@/assets/svg/common/information.svg')
      background-position: center
      background-repeat: no-repeat
      background-size: auto

  .button-primary-container
    display: flex
    justify-content: center
    margin-bottom: 20px

  .identification-myid
    font-weight: 500
    font-size: 16px
    line-height: 20px

    p
      color: #FFFFFF
      opacity: 0.5

    button
      width: 100%
      background: #FFFFFF
      box-shadow: 0px 0px 4px rgba(0, 0, 0, .15)
      padding: 10px
      border-radius: 50px
      display: flex
      justify-content: center
</style>
